import React from 'react';
import { Navigate, useSearchParams, useLocation } from 'react-router-dom';
import { Box, Alert, Typography } from '@mui/material';
import { useAuth } from 'contexts/AuthContext';
import { PortalTenantProvider } from '../../contexts/PortalTenantContext';
import { adminUrlTenant, resolveAdminPortalTenantId } from '../../utils/portalAdminTenantOverride';
import { getRuntimeConfig } from '../../runtime/runtimeConfig';

interface PortalAccessGuardProps {
  children: React.ReactNode;
}

const PortalAccessGuard: React.FC<PortalAccessGuardProps> = ({ children }) => {
  const { user: authUser } = useAuth();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  // A ?tenant=<id> deep-link (e.g. the supplier-credential handoff) or, on the HITL
  // review, the handoff token (see adminUrlTenant) lets an admin enter a specific
  // tenant's portal — PortalTenantProvider scopes to it — even when the admin has
  // no tenant role of their own. Same resolution as the provider, so they agree.
  const adminTenantOverride = authUser?.role === 'admin'
    ? resolveAdminPortalTenantId(adminUrlTenant(searchParams, location.pathname))
    : null;
  const hasAdminTenantOverride = Boolean(adminTenantOverride);
  const runtime = getRuntimeConfig();

  // Tenant mode: this origin serves exactly one tenant. Admins are let in
  // without a tenant role (the backend accepts them); internal staff belong on
  // the admin app; portal users of another tenant never get here, because /me
  // already refused them at login.
  if (runtime.mode === 'tenant') {
    if (authUser?.role === 'tenant_user') {
      return (
        <Box sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Alert severity="info" sx={{ maxWidth: 600 }}>
            <Typography variant="h6" gutterBottom>
              This is a customer portal
            </Typography>
            <Typography>
              Internal accounts use the admin app{runtime.admin_url ? (
                <>: <a href={runtime.admin_url}>{runtime.admin_url}</a></>
              ) : '.'}
            </Typography>
          </Alert>
        </Box>
      );
    }
    return (
      <PortalTenantProvider>
        {children}
      </PortalTenantProvider>
    );
  }

  // Admins can access the portal (for debugging)
  if (authUser?.role === 'admin') {
    if (!authUser.tenant_roles?.length && !hasAdminTenantOverride) {
      return (
        <Box sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Alert severity="warning" sx={{ maxWidth: 600 }}>
            <Typography variant="h6" gutterBottom>
              No Tenant Configured
            </Typography>
            <Typography>
              Admin accounts need at least one tenant role to use the portal. Please configure a tenant role in User Management.
            </Typography>
          </Alert>
        </Box>
      );
    }
    return (
      <PortalTenantProvider>
        {children}
      </PortalTenantProvider>
    );
  }

  // tenant_users should use the admin UI
  if (authUser?.role === 'tenant_user') {
    return <Navigate to="/" replace />;
  }

  // end_users need at least one tenant_role
  if (!authUser?.tenant_roles?.length) {
    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Alert severity="error" sx={{ maxWidth: 600 }}>
          <Typography variant="h6" gutterBottom>
            No Tenant Configured
          </Typography>
          <Typography>
            Your account does not have a tenant configured. Please contact your administrator.
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <PortalTenantProvider>
      {children}
    </PortalTenantProvider>
  );
};

export default PortalAccessGuard;
